import { Component, effect, model } from "@angular/core";
import { finalize } from "rxjs";
import { Crop } from "../../models/crop.model";
import { Client } from "../../models/client.model";
import { DataService } from "../../services/api/data/data.service";
import { ModalService } from "../../services/modal/modal.service";
import { FieldViewEntityComponent } from "../../modals/field-view-entity/field-view-entity.component";
import { MapComponent } from "../map/map.component";
import { AuthService } from "../../services/api/auth/auth.service";
import { LoadingService } from "../../services/loading/loading";
import { currentDate } from "../map/util/utliWaterYear";
import { FieldFeatureCollection } from "../../models/fields.model";

@Component({
  selector: "app-field-claim-entity",
  standalone: true,
  imports: [MapComponent, FieldViewEntityComponent],
  templateUrl: "./field-claim-entity.component.html",
  styleUrl: "./field-claim-entity.component.scss",
  host: {
    class: "flex flex-col h-full overflow-hidden",
  },
})
export class FieldClaimEntityComponent {
  crops?: Crop[];
  clients: Client[] = [];
  fields?: FieldFeatureCollection;
  clientId = model<string>("17"); // ! Default to cawelo
  properties?: any;
  isDarkMode = false;
  isUpdateZoomLevel = true;
  private fieldsRequestSeq = 0;
  private readonly defaultMapEtDate = currentDate().format("YYYY-MM-DD");

  constructor(
    private dataService: DataService,
    private modalService: ModalService,
    private authService: AuthService,
    private loadingService: LoadingService,
  ) {
    effect(() => {
      this.onClientChange(this.clientId()); // Change client when clientId changes
    });
  }

  onClientChange(clientId: string) {
    this.isUpdateZoomLevel = true;
    this.clientId.set(clientId);
    this.fetchFields(clientId);
  }

  onCreateUpdateCropRecords() {
    this.isUpdateZoomLevel = false;
    if (this.clientId()) {
      this.fetchFields(this.clientId());
    }
  }

  selectUpdateModalShow(properties: string) {
    const propertyObj = JSON.parse(properties);
    if (this.properties && this.properties.id === propertyObj.id) {
      this.modalService.toggleFieldViewEntityModal();
    } else {
      this.properties = JSON.parse(properties);
    }
  }

  ngOnInit(): void {
    const token = this.authService.getToken();
    if (!token) return;
    const decode = this.authService.getDecodedToken();
    const clientId: string = decode["signup_client_id"]; // Cawelo as default
    if (clientId) {
      this.clientId.set(clientId);
    }

    // fetch all crops
    this.fetchCrops();
    // fetch all clients
    this.fetchClients();
    // fetch all field boundaries
    this.fetchFields(this.clientId());
    // Initialize dark mode based on the current state in localStorage
    this.isDarkMode = localStorage.getItem("darkMode") === JSON.stringify(true);
    // Listen for localStorage changes in this tab
    window.addEventListener("storage", this.onStorageChange.bind(this));
  }

  onStorageChange(event: StorageEvent) {}

  ngOnDestroy() {
    // Remove the event listener when the component is destroyed
    window.removeEventListener("storage", this.onStorageChange.bind(this));
  }

  // Getting all crops by calling dataService
  fetchCrops(): void {
    this.loadingService.start({
      token: "field-claim-entity-crops",
      message: "Loading crops",
    });
    this.dataService
      .getAllCrops()
      .pipe(
        finalize(() => this.loadingService.stop("field-claim-entity-crops")),
      )
      .subscribe({
        next: (crops: Crop[]) => {
          this.crops = crops;
        },
        error: (error: any) => {
          console.error("Error fetching crops data", error);
        },
      });
  }

  // Getting clients with shared fields for entity users
  fetchClients(): void {
    this.loadingService.start({
      token: "field-claim-entity-clients",
      message: "Loading clients",
    });
    this.dataService
      .getAllClients()
      .pipe(
        finalize(() => this.loadingService.stop("field-claim-entity-clients")),
      )
      .subscribe({
        next: (clients: Client[]) => {
          this.clients = clients;
        },
        error: (error: any) => {
          console.error("Error fetching clients data", error);
        },
      });
  }

  // Getting all field boundaries by calling dataService
  fetchFields(clientId?: string): void {
    clientId = !clientId ? "" : clientId;
    const requestSeq = ++this.fieldsRequestSeq;
    this.loadingService.start({
      token: "field-claim-entity-fields",
      message: "Loading fields",
    });
    this.dataService
      .getAllFieldsForDate(clientId, this.defaultMapEtDate)
      .pipe(
        finalize(() => this.loadingService.stop("field-claim-entity-fields")),
      )
      .subscribe({
        next: (fields: FieldFeatureCollection) => {
          if (requestSeq !== this.fieldsRequestSeq) return;
          this.fields = fields;
          this.hydrateFieldsHistory(clientId, requestSeq);
        },
        error: (error: any) => {
          console.error("Error fetching GeoJSON data", error);
        },
      });
  }

  private hydrateFieldsHistory(clientId: string, requestSeq: number): void {
    this.dataService.getAllFields(clientId).subscribe({
      next: (fields: FieldFeatureCollection) => {
        if (requestSeq !== this.fieldsRequestSeq) return;
        this.fields = fields;
      },
      error: (error: any) => {
        console.error("Error hydrating GeoJSON ET history", error);
      },
    });
  }
}
